"use client";

import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  adminListTenants,
  listApiKeys,
  createApiKey,
  revokeApiKey,
  getApiKeyUsage,
  type ApiKeyRecord,
  type Tenant,
  type UsageLog,
} from "@/lib/api";
import { formatTime } from "@/lib/utils";
import { RequirePlatformAdmin } from "@/components/auth/require-platform-admin";
import { TransferDialog } from "@/components/transfer-dialog";
import { useAuth } from "@/components/auth/auth-provider";

export default function ApiKeysPage() {
  const { user } = useAuth();
  const [keys, setKeys] = useState<ApiKeyRecord[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [targetTenant, setTargetTenant] = useState("");
  const [createdKey, setCreatedKey] = useState("");
  const [expandedId, setExpandedId] = useState("");
  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);

  const [reloadTick, setReloadTick] = useState(0);
  const reload = () => setReloadTick((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    listApiKeys().then((res) => {
      if (!cancelled) setKeys(res.keys);
    });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  useEffect(() => {
    if (!user?.is_platform_admin) return;
    let cancelled = false;
    adminListTenants().then((list) => {
      if (cancelled) return;
      setTenants(list);
      if (list.length > 0) setTargetTenant((prev) => prev || list[0].id);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.is_platform_admin]);

  async function handleCreate() {
    if (!name.trim()) {
      toast.error("请填写名称");
      return;
    }
    if (!targetTenant) {
      toast.error("请选择租户");
      return;
    }
    const res = await createApiKey(name.trim(), note.trim(), targetTenant);
    setCreatedKey(res.key);
    setName("");
    setNote("");
    toast.success(`已为租户 ${targetTenant} 创建 API 密钥`);
    reload();
  }

  async function handleRevoke(id: string) {
    await revokeApiKey(id);
    toast.success("API 密钥已撤销");
    reload();
  }

  function copyKey() {
    navigator.clipboard.writeText(createdKey);
    toast.success("已复制到剪贴板");
  }

  async function toggleExpand(id: string) {
    if (expandedId === id) {
      setExpandedId("");
      return;
    }
    setExpandedId(id);
    const res = await getApiKeyUsage(id);
    setUsageLogs(res.logs);
  }

  return (
    <div className="flex min-h-full flex-col gap-6 p-6">
      <h1 className="text-lg font-semibold">API 密钥</h1>

      <RequirePlatformAdmin>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">租户</label>
            <Select value={targetTenant} onValueChange={setTargetTenant}>
              <SelectTrigger size="sm" className="w-56">
                <SelectValue placeholder="请选择租户..." />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">名称</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例如：生产环境"
              className="w-48"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">备注</label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="备注（可选）"
              className="w-64"
            />
          </div>
          <Button onClick={handleCreate} size="sm">
            创建 API 密钥
          </Button>
        </div>
      </RequirePlatformAdmin>

      {/* Show newly created key */}
      {createdKey && (
        <div className="flex items-center gap-3 rounded-md border border-green-300 bg-green-50 p-3 dark:border-green-800 dark:bg-green-950">
          <code className="flex-1 text-sm font-mono break-all">{createdKey}</code>
          <Button variant="ghost" size="sm" onClick={copyKey}>
            <Copy className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setCreatedKey("")}>
            关闭
          </Button>
          <span className="text-xs text-muted-foreground">请立即妥善保存此密钥，之后将不再显示。</span>
        </div>
      )}

      {/* Keys table */}
      <div className="rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              <th className="px-4 py-2 text-left font-medium w-8"></th>
              <th className="px-4 py-2 text-left font-medium">名称</th>
              <th className="px-4 py-2 text-left font-medium">备注</th>
              <th className="px-4 py-2 text-left font-medium">创建时间</th>
              <th className="px-4 py-2 text-left font-medium">对话</th>
              <th className="px-4 py-2 text-left font-medium">最近使用</th>
              <th className="px-4 py-2 text-left font-medium">状态</th>
              <th className="px-4 py-2 text-left font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <Fragment key={k.id}>
                <tr className="border-b hover:bg-muted/20">
                  <td className="px-4 py-2">
                    <button onClick={() => toggleExpand(k.id)} className="text-muted-foreground hover:text-foreground">
                      {expandedId === k.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="px-4 py-2 font-medium">{k.name}</td>
                  <td className="px-4 py-2 text-muted-foreground">{k.note || "-"}</td>
                  <td className="px-4 py-2">{formatTime(k.created_at)}</td>
                  <td className="px-4 py-2">{k.total_calls}</td>
                  <td className="px-4 py-2">{k.last_used_at ? formatTime(k.last_used_at) : "-"}</td>
                  <td className="px-4 py-2">
                    {k.active ? (
                      <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">启用</Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-neutral-100 text-neutral-800 dark:bg-neutral-900 dark:text-neutral-200">已撤销</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {k.active ? (
                      <RequirePlatformAdmin fallback={<span className="text-xs text-muted-foreground">-</span>}>
                        <div className="flex items-center gap-2">
                          <TransferDialog
                            resource="api-keys"
                            resourceId={k.id}
                            resourceLabel={k.name}
                            currentTenantId={k.tenant_id}
                            tenants={tenants}
                            onTransferred={reload}
                          />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm">撤销</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>撤销 API 密钥</AlertDialogTitle>
                                <AlertDialogDescription>
                                  此操作将永久禁用密钥“{k.name}”。使用此密钥的 API 请求将失败。
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>取消</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleRevoke(k.id)}>撤销</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </RequirePlatformAdmin>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </td>
                </tr>
                {expandedId === k.id && (
                  <tr className="border-b">
                    <td colSpan={8} className="px-8 py-3 bg-muted/10">
                      <p className="mb-2 text-xs font-semibold text-muted-foreground">最近使用记录</p>
                      {usageLogs.length === 0 ? (
                        <p className="text-xs text-muted-foreground">暂无使用记录</p>
                      ) : (
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-muted-foreground">
                              <th className="pr-4 py-1 text-left">时间</th>
                              <th className="pr-4 py-1 text-left">接口地址</th>
                              <th className="pr-4 py-1 text-left">对话 ID</th>
                              <th className="pr-4 py-1 text-left">状态</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usageLogs.map((l) => (
                              <tr key={l.id}>
                                <td className="pr-4 py-1">{formatTime(l.created_at)}</td>
                                <td className="pr-4 py-1 font-mono">{l.endpoint}</td>
                                <td className="pr-4 py-1 font-mono">{l.call_id || "-"}</td>
                                <td className="pr-4 py-1">{l.status_code}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {keys.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">
                  暂无 API 密钥，请在上方创建。
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
